import React from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import { observer } from 'mobx-react';
import { useFormikContext } from 'formik';

import { useBasisTheoryToken } from 'common/hooks/use-basis-theory-token';
import infoDarkGreen from 'core/assets/svg/info-dark-green.svg';
import BaseDialog from 'core/components/base-dialog';
import { Label } from 'core/components/label';
import { NotificationCard } from 'core/components/notification-card';
import { useToast } from 'core/components/toast';
import { useStore } from 'core/store';
import useSession from 'core/hooks/use-session';
import { PolicyTabs } from 'core/store/account-store/policies-store/policy-store/policy-store';
import CreditCardForm from 'customer/components/policy/payment-tab/credit-card-form/credit-card-form';
import useStyles from './add-secondary-credit-card-modal.styles';

const AddSecondaryCreditCardModal = ({ open, onClose }) => {
  const classes = useStyles();
  const toast = useToast();
  const { values } = useFormikContext();
  const { isAgency } = useSession();

  const {
    account: {
      fetchFullAccountAndPolicy,
      id: accountId,
      policies: {
        policy: {
          policy: { id },
          addCreditCard,
          setCurrentTab,
          isCancelledOrInCancellation
        }
      }
    }
  } = useStore();
  const { getBasisTheoryCCToken } = useBasisTheoryToken();

  const handleAddCard = async () => {
    try {
      const { token } = await getBasisTheoryCCToken();
      const { success, message, card } = await addCreditCard({
        accountId,
        policyId: id,
        basisTheoryCardToken: token,
        customerId: values.stripeCustomerId,
        isPrimary: false
      });
      const externalAgentLabelText =
        isCancelledOrInCancellation && isAgency
          ? 'To process payments, contact support.'
          : 'To process payments, go to payment and price tab.';

      toast.notify({
        type: success ? 'success' : 'error',
        message,
        label: externalAgentLabelText,
        action: () => setCurrentTab(PolicyTabs.PAYMENT),
        durationInSeconds: 10
      });

      if (success && values.stripeCustomerId !== card.customerId) {
        fetchFullAccountAndPolicy(id, accountId);
      }
    } catch (error) {
      toast.notify({
        type: 'error',
        message: `An error occurred. ${error.message.replace(':', '').trim()}`
      });
    }
  };

  const userTip =
    isAgency && isCancelledOrInCancellation
      ? 'To process payments with this method, contact support after adding it, since this policy is in cancellation or has been canceled.'
      : 'To process payments with this method, go to the payment and price tab after adding it.';

  return (
    <BaseDialog open={open} onClose={onClose} className={classes.container}>
      <div className={classes.content}>
        <Label type="h1" className={classes.title}>
          Add an alternate payment method
        </Label>
        <div>
          <CreditCardForm
            onSubmit={handleAddCard}
            buttonText="Add"
            label="Card information"
            mode="dark"
            fromModal
            closeModal={onClose}
          >
            <Grid container className={classes.callouts}>
              <NotificationCard className={classes.notification} type="light" icon={infoDarkGreen} iconSize="icon16">
                <Label type="body3">
                  This payment method will be saved to the member’s profile for future use. It won’t replace the primary
                  method or trigger an immediate charge. Use an alternate payment method when you need to make a
                  one-time payment using a different method than the primary one.
                </Label>
              </NotificationCard>
              <div className={classes.tip}>
                <img src={infoDarkGreen} alt="info" />
                <Label type="notificationText">
                  <span style={{ fontWeight: 600 }}>User tip:</span> {userTip}
                </Label>
              </div>
            </Grid>
          </CreditCardForm>
        </div>
      </div>
    </BaseDialog>
  );
};

AddSecondaryCreditCardModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default observer(AddSecondaryCreditCardModal);
